// =============================================================================
// SIREPPAR - Cuadro de nota de la Secretaría
// =============================================================================
// El mismo cuadro en todos lados: fondo amarillo, textarea redimensionable y el
// botón "Anotar" DENTRO, abajo a la derecha. Que esté adentro no es capricho —es
// como se hizo en la columna Nota de /partidos— y tiene un motivo: un botón al
// costado le roba ancho a la nota, que es lo que hay que poder leer.
//
// Se guarda con el botón, NO al salir del campo: guardar sin que la persona lo pida
// hace que un clic de paso por el textarea escriba en la base sin querer.
//
// Estaba escrito dentro de TableroDocumentacion (NotaCelda). Se extrajo acá cuando
// las secciones de alianza necesitaron el mismo cuadro: copiar el markup era
// garantizar que los dos se fueran separando con cada retoque.
// =============================================================================

import { useEffect, useState } from 'react';

interface Props {
  valor: string;
  /** Guarda la nota. Debe devolver la promesa para poder mostrar "Guardando…". */
  onGuardar: (nota: string) => Promise<unknown>;
  puedeEditar: boolean;
  placeholder?: string;
  rows?: number;
  /** Marca opcional en el textarea, para poder ubicarlo desde afuera. */
  dataAttr?: Record<string, string>;
  /** Texto cuando no hay nota y no se puede editar. */
  vacio?: React.ReactNode;
}

export default function CuadroNota({
  valor, onGuardar, puedeEditar, placeholder = 'Nota...', rows = 2, dataAttr, vacio,
}: Props) {
  const [texto, setTexto] = useState(valor);
  const [guardando, setGuardando] = useState(false);

  // Si la nota cambió en la base (se guardó, o la editó otra sesión), el campo la
  // toma. Sólo corre cuando el string cambia, así que no pisa lo que se escribe.
  useEffect(() => { setTexto(valor); }, [valor]);

  const sucio = texto.trim() !== (valor || '').trim();

  const guardar = async () => {
    if (!sucio || guardando) return;
    setGuardando(true);
    try { await onGuardar(texto); } finally { setGuardando(false); }
  };

  if (!puedeEditar) {
    return texto
      ? (
        <p className="bg-yellow-50 border border-gray-400 rounded-md px-2.5 py-1.5 text-xs text-gray-800 whitespace-pre-wrap leading-relaxed">
          {texto}
        </p>
      )
      : <>{vacio ?? <span className="text-xs text-gray-300">—</span>}</>;
  }

  return (
    <div className="relative min-w-0">
      <textarea
        {...dataAttr}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={4000}
        className="block w-full resize-y border border-gray-400 rounded-md pl-2.5 pr-2.5 pt-1.5 pb-7 text-xs
          leading-relaxed bg-yellow-50 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      <button
        type="button"
        onClick={guardar}
        disabled={!sucio || guardando}
        title={sucio ? 'Guardar la nota' : 'No hay cambios para guardar'}
        className="absolute bottom-1 right-1 border border-black text-black bg-white text-[11px] leading-none
          px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {guardando ? 'Guardando…' : 'Anotar'}
      </button>
    </div>
  );
}
